import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, Checkbox, FormControlLabel,
    CircularProgress, Alert, Divider, Chip, Dialog, DialogTitle,
    DialogContent, DialogActions
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import axios from 'axios';

// Instância pública — sem interceptors de autenticação
const apiPublica = axios.create({
    baseURL: process.env.REACT_APP_API_URL || '/api'
});

export default function AceiteEletronico() {
    const { token } = useParams();

    const [documento, setDocumento] = useState(null);
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState(null);

    const [aceitouConteudo, setAceitouConteudo] = useState(false);
    const [aceitouLGPD, setAceitouLGPD] = useState(false);

    const [modalAberto, setModalAberto] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const [concluido, setConcluido] = useState(false);

    useEffect(() => {
        apiPublica.get(`/public/aceite/${token}`)
            .then(res => setDocumento(res.data))
            .catch(() => setErro('Link inválido ou expirado. Solicite um novo documento ao seu psicólogo.'))
            .finally(() => setLoading(false));
    }, [token]);

    const handleConfirmarAceite = async () => {
        setEnviando(true);
        try {
            await apiPublica.post(`/public/aceite/${token}`, {
                aceitouTermosLGPD: aceitouLGPD,
                aceitouConteudo,
            });
            setModalAberto(false);
            setConcluido(true);
        } catch (e) {
            setModalAberto(false);
            setErro(e.response?.data?.message || 'Erro ao registrar aceite. Tente novamente.');
        } finally {
            setEnviando(false);
        }
    };

    // ── Loading ──
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f7fa' }}>
                <CircularProgress sx={{ color: '#1ABB9C' }} />
            </Box>
        );
    }

    // ── Link inválido ──
    if (erro && !documento) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f7fa', p: 2 }}>
                <Paper sx={{ p: 4, maxWidth: 480, textAlign: 'center', borderRadius: 3 }}>
                    <GavelIcon sx={{ fontSize: 56, color: '#ef4444', mb: 2 }} />
                    <Typography variant="h6" fontWeight="bold" gutterBottom>Link inválido</Typography>
                    <Typography color="text.secondary">{erro}</Typography>
                </Paper>
            </Box>
        );
    }

    // ── Já assinado anteriormente ──
    if (documento?.assinado && !concluido) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f7fa', p: 2 }}>
                <Paper sx={{ p: 4, maxWidth: 480, textAlign: 'center', borderRadius: 3 }}>
                    <CheckCircleIcon sx={{ fontSize: 64, color: '#1ABB9C', mb: 2 }} />
                    <Typography variant="h6" fontWeight="bold" gutterBottom>Documento já assinado</Typography>
                    <Typography color="text.secondary">
                        Este documento já foi aceito eletronicamente e não pode ser assinado novamente.
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', mt: 2 }} color="text.secondary">
                        Data do aceite: {documento.dataAssinatura
                            ? new Date(documento.dataAssinatura).toLocaleString('pt-BR')
                            : '-'}
                    </Typography>
                </Paper>
            </Box>
        );
    }

    // ── Concluído agora ──
    if (concluido) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f0fdf4', p: 2 }}>
                <Paper sx={{ p: 4, maxWidth: 520, textAlign: 'center', borderRadius: 3 }}>
                    <CheckCircleIcon sx={{ fontSize: 72, color: '#16a34a', mb: 2 }} />
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                        Aceite registrado com sucesso!
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                        Seu aceite eletrônico foi registrado em{' '}
                        <strong>{new Date().toLocaleString('pt-BR')}</strong>.
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    <Alert severity="success" icon={<VerifiedUserIcon />}>
                        Este aceite possui validade jurídica conforme a <strong>Lei 13.709/2018 (LGPD)</strong> e a{' '}
                        <strong>MP 2.200-2/2001</strong>. Guarde este comprovante.
                    </Alert>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                        Protocolo: <strong>{documento?.numeroDocumento}</strong>
                    </Typography>
                </Paper>
            </Box>
        );
    }

    // ── Página principal ──
    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa', py: 4, px: 2 }}>
            <Box sx={{ maxWidth: 720, mx: 'auto' }}>

                {/* Cabeçalho */}
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <GavelIcon sx={{ fontSize: 48, color: '#1ABB9C', mb: 1 }} />
                    <Typography variant="h5" fontWeight="bold">{documento.finalidade}</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                        <Chip label={`Nº ${documento.numeroDocumento}`} size="small" variant="outlined" />
                        <Chip label={`Paciente: ${documento.nomePaciente}`} size="small" variant="outlined" />
                        {documento.nomePsicologo && (
                            <Chip label={`Psicólogo: ${documento.nomePsicologo}`} size="small" variant="outlined" />
                        )}
                        <Chip
                            label={`Emitido em: ${new Date(documento.dataEmissao).toLocaleDateString('pt-BR')}`}
                            size="small"
                            variant="outlined"
                        />
                    </Box>
                </Box>

                {/* Conteúdo do documento */}
                <Paper elevation={0} sx={{ p: { xs: 2, sm: 4 }, borderRadius: 2, mb: 3, boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
                        Conteúdo do Documento
                    </Typography>
                    <Divider sx={{ mb: 3 }} />

                    {documento.cabecalhoFinal && (
                        <Box
                            dangerouslySetInnerHTML={{ __html: documento.cabecalhoFinal }}
                            sx={{
                                mb: 3,
                                pb: 2,
                                borderBottom: '1px solid #e2e8f0',
                                '& img': { maxWidth: '100%', height: 'auto' },
                                '& table': { borderCollapse: 'collapse', width: '100%', mb: 1 },
                                '& td, & th': { border: '1px solid #999', padding: '4px 8px', verticalAlign: 'top' },
                                '& .table-borderless td, & .table-borderless th': { border: 'none' },
                            }}
                        />
                    )}

                    <Box
                        dangerouslySetInnerHTML={{ __html: documento.conteudoFinal }}
                        sx={{
                            fontFamily: 'serif',
                            lineHeight: 1.9,
                            fontSize: '0.97rem',
                            '& h1': { fontSize: '1.6rem', fontWeight: 'bold', mt: 2, mb: 1 },
                            '& h2': { fontSize: '1.35rem', fontWeight: 'bold', mt: 2, mb: 1 },
                            '& h3': { fontSize: '1.15rem', fontWeight: 'bold', mt: 1.5, mb: 0.5 },
                            '& h4': { fontSize: '1rem', fontWeight: 'bold', mt: 1, mb: 0.5 },
                            '& p': { mb: 1 },
                            '& ul, & ol': { pl: 3, mb: 1 },
                            '& strong': { fontWeight: 'bold' },
                            '& em': { fontStyle: 'italic' },
                            '& u': { textDecoration: 'underline' },
                            '& s': { textDecoration: 'line-through' },
                            '& table': { borderCollapse: 'collapse', width: '100%', mb: 2 },
                            '& td, & th': { border: '1px solid #999', padding: '5px 8px', verticalAlign: 'top' },
                            '& th': { background: '#f5f5f5', fontWeight: 'bold' },
                            '& .table-borderless td, & .table-borderless th': { border: 'none' },
                            '& .table-borderless th': { background: 'transparent' },
                            '& img': { maxWidth: '100%', height: 'auto' },
                        }}
                    />
                </Paper>

                {/* Seção de aceite */}
                <Paper elevation={0} sx={{ p: { xs: 2, sm: 4 }, borderRadius: 2, mb: 3, boxShadow: '0 2px 10px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                        <VerifiedUserIcon sx={{ color: '#1ABB9C' }} />
                        <Typography variant="h6" fontWeight="bold">Aceite Eletrônico</Typography>
                    </Box>

                    <Alert severity="info" sx={{ mb: 3 }}>
                        Este aceite possui validade jurídica conforme a <strong>Lei nº 13.709/2018 (LGPD)</strong> e a{' '}
                        <strong>MP 2.200-2/2001</strong>. Ao confirmar, você declara que leu e concorda com os termos acima.
                    </Alert>

                    {erro && <Alert severity="error" sx={{ mb: 3 }}>{erro}</Alert>}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={aceitouConteudo}
                                    onChange={(e) => setAceitouConteudo(e.target.checked)}
                                    sx={{ color: '#1ABB9C', '&.Mui-checked': { color: '#1ABB9C' } }}
                                />
                            }
                            label={
                                <Typography variant="body2">
                                    Li e compreendi integralmente o conteúdo deste documento e concordo com todos os seus termos.
                                </Typography>
                            }
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={aceitouLGPD}
                                    onChange={(e) => setAceitouLGPD(e.target.checked)}
                                    sx={{ color: '#1ABB9C', '&.Mui-checked': { color: '#1ABB9C' } }}
                                />
                            }
                            label={
                                <Typography variant="body2">
                                    Autorizo o uso dos meus dados pessoais para finalidade terapêutica, nos termos da{' '}
                                    <strong>Lei 13.709/2018 (LGPD)</strong>, podendo revogar este consentimento a qualquer momento.
                                </Typography>
                            }
                        />
                    </Box>

                    <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        disabled={!aceitouConteudo || !aceitouLGPD}
                        onClick={() => setModalAberto(true)}
                        startIcon={<CheckCircleIcon />}
                        sx={{
                            bgcolor: '#1ABB9C',
                            '&:hover': { bgcolor: '#129A80' },
                            py: 1.8,
                            fontSize: '1rem',
                            fontWeight: 'bold',
                        }}
                    >
                        Confirmar Aceite
                    </Button>

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
                        Ao confirmar, seu aceite será registrado com data, hora e protocolo do documento.
                    </Typography>
                </Paper>
            </Box>

            {/* Modal de confirmação */}
            <Dialog open={modalAberto} onClose={() => !enviando && setModalAberto(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningAmberIcon sx={{ color: '#f59e0b' }} />
                    Confirmar aceite eletrônico
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        Você está prestes a confirmar o aceite eletrônico do documento{' '}
                        <strong>{documento?.finalidade}</strong> (Protocolo: {documento?.numeroDocumento}).
                    </Typography>
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        Esta ação <strong>não pode ser desfeita</strong>. Certifique-se de ter lido todo o conteúdo antes de confirmar.
                    </Alert>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setModalAberto(false)} disabled={enviando} color="inherit">
                        Voltar e revisar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmarAceite}
                        disabled={enviando}
                        startIcon={enviando ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                        sx={{ bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#129A80' } }}
                    >
                        {enviando ? 'Registrando...' : 'Sim, confirmar aceite'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
